import styles from './CardModule.scss';

import cx from 'classnames';
import React from 'react';

import { ReferencedEntry } from 'contentful';
import { CardAssembly, Topic } from 'src/api/contentful/contentTypes.cloverdotcom';
import { Entries } from 'src/api/contentful/utilTypes';
import { componentKey, isEntry } from 'src/utils/EntryUtil';

import { FadeToastUp } from '../Animation/FadeToastUp/FadeToastUp';
import { Card } from '../Card/Card';
import { TextV2 } from '../TextV2/TextV2';
import { CardTextStack } from './CardTextStack';

interface CardModuleProps extends React.AllHTMLAttributes<HTMLElement> {
  items?: Entries<Topic>;
  hideMainTitle?: boolean;
  isFloating?: boolean;
  isCtaButton?: boolean;
  title?: string;
  cardBackgroundColor?: string;
  customCardStyle?: string;
  disableAnimation?: boolean;
}

const CardModule: React.FC<CardModuleProps> = ({
  items = [],
  hideMainTitle = false,
  isFloating = false,
  isCtaButton = false,
  cardBackgroundColor,
  customCardStyle,
  title,
  className,
  disableAnimation,
  ...props
}) => {
  if (!items.length) return null;

  return (
    <section className={cx(styles.cardModuleWrapper, className)} {...props}>
      <FadeToastUp disableAnimation={disableAnimation} threshold={.1}>
        <div className={cx(styles.cardModule, { [styles.cardModuleFloating]: isFloating })}>

          {!hideMainTitle && title && (
            <FadeToastUp<HTMLHeadingElement>>
              {({ ref, className: fadeToastUpClassName, style: fadeToastUpStyle }) => (
                <TextV2
                  hasId
                  className={cx(fadeToastUpClassName, styles.mainTitle)}
                  style={fadeToastUpStyle}
                  ref={ref}
                  value={title}
                  tagName="h2"
                />
              )}
            </FadeToastUp>
          )}

          {items.map((item, index) => (
            <Card
              entry={item}
              key={componentKey(item, index)}
              className={cx(styles.card, customCardStyle)}
              mediaClassName={cx(styles.media)}
              style={{ backgroundColor: cardBackgroundColor }}
            >
              <CardTextStack entry={item} isCtaButton={isCtaButton} />
            </Card>
          ))}
        </div>
      </FadeToastUp>
    </section>
  );
};

interface TextMediaModuleContainerProps extends CardModuleProps {
  entry?: ReferencedEntry<CardAssembly>;
}

// container card module component for general side by side module with set cotainer size.
// The card module above could be used as a presentational component and is full width by default
const CardModuleContainer: React.FC<TextMediaModuleContainerProps> = ({ entry, className, style, ...props }) => {
  if (!isEntry(entry)) return null;

  const { isFloating, isCtaButton, cardBackgroundColor, title, items } = entry.fields;

  return (
    <CardModule
      items={items}
      style={{ ...style }}
      {...{ isFloating, cardBackgroundColor, isCtaButton, title }}
      className={cx(styles.cardModuleWrapperDefault, className)}
      {...props}
    />
  );
};

export {
  CardModuleContainer,
  CardModule,
};
